import { useCallback, useEffect, useState } from 'react';
import * as orgApi from '../api/organizations';
import { useToast } from './toast';
import { Spinner, ErrorAlert, EmptyState, TextareaField, formatDateTime } from './ui';
import { cn, ui } from '../lib/ui';
import { PROFILE_COPY, PROFILE_FIELDS, orgTypePolicy } from '../lib/orgTypes';
import { fieldErrorsFrom } from '../lib/formErrors';

const FILTERS = [
  { id: 'submitted', label: 'Waiting for review' },
  { id: 'all', label: 'All with a profile' },
];

function display(def, value) {
  if (value === undefined || value === null || value === '') return '-';
  if (def.kind === 'boolean' || def.kind === 'accept') return value ? 'Yes' : 'No';
  if (def.kind === 'choice') return def.options[value] || value;
  return String(value);
}

function ProfileTable({ profileType, profile, verified }) {
  const fields = PROFILE_FIELDS[profileType];
  // A table wider than a phone scrolls; the region takes focus so it can be
  // scrolled from the keyboard.
  return (
    <div className={ui.tableWrap} tabIndex={0} role="region" aria-label="Submitted profile">
      <table className={ui.table}>
        <caption className="sr-only">Submitted profile</caption>
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Submitted</th>
            {verified && <th scope="col">Verified</th>}
          </tr>
        </thead>
        <tbody>
          {Object.entries(fields).map(([key, def]) => {
            const now = display(def, profile?.[key]);
            const before = verified ? display(def, verified[key]) : null;
            return (
              <tr key={key}>
                <td className="align-top">{PROFILE_COPY[profileType][key].label}</td>
                <td className={cn('align-top', verified && now !== before && 'font-semibold')}>
                  {now}
                </td>
                {verified && <td className="align-top">{before}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReviewCard({ org, onReviewed }) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(null);
  const [busy, setBusy] = useState(null);
  const c = org.compliance;
  const policy = orgTypePolicy(org.orgType);

  async function review(decision) {
    if (decision === 'return' && note.trim().length < 5) {
      setNoteError('Say what the organization needs to change');
      return;
    }
    setBusy(decision);
    try {
      const data = await orgApi.updateOrganization(org.orgId, {
        complianceReview: { decision, note: note.trim() || undefined },
      });
      toast.success(decision === 'verify' ? `${org.name} verified` : `${org.name} returned`);
      onReviewed(data.organization);
    } catch (err) {
      setNoteError(fieldErrorsFrom(err)['complianceReview.note'] || null);
      toast.error(err.message || 'Review failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <article className={cn(ui.card, 'mb-4')} aria-label={org.name}>
      <div className={cn(ui.row, ui.spread, 'mb-2')}>
        <div>
          <h3 className="m-0 text-title-s">{org.name}</h3>
          <p className={cn(ui.hint, 'm-0')}>
            {policy?.label || 'No type'} · {org.slug} · submitted{' '}
            {c.submittedAt ? formatDateTime(c.submittedAt) : '-'}
          </p>
        </div>
        <span className={c.status === 'submitted' ? ui.pillInfo : ui.pillNeutral}>{c.status}</span>
      </div>
      <ProfileTable
        profileType={c.profileType}
        profile={c.profile}
        verified={c.verified?.profile && c.status !== 'verified' ? c.verified.profile : null}
      />
      {c.profileType === 'nonprofit' && (
        <p className={cn(ui.hint, 'mt-2')}>
          Check the EIN against the IRS Tax Exempt Organization Search and, when it solicits in
          California, the Registry number and its status in the Attorney General Registry search.
        </p>
      )}
      {c.status === 'submitted' && (
        <>
          <TextareaField
            label="Note to the organization (needed to return it)"
            value={note}
            onChange={e => {
              setNote(e.target.value);
              setNoteError(null);
            }}
            rows={2}
            maxLength={1000}
            error={noteError}
          />
          <div className={ui.row}>
            <button
              type="button"
              className={ui.btnPrimary}
              disabled={Boolean(busy)}
              onClick={() => review('verify')}
            >
              {busy === 'verify' ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              className={ui.btnSecondary}
              disabled={Boolean(busy)}
              onClick={() => review('return')}
            >
              {busy === 'return' ? 'Returning...' : 'Return for changes'}
            </button>
          </div>
        </>
      )}
    </article>
  );
}

/**
 * Platform admin -> Compliance (docs/Decisions.md D64): the nonprofit and
 * investment profiles organizations submitted, to verify (which opens
 * donations or member services) or return with a note.
 */
export default function ComplianceReviewPanel() {
  const [filter, setFilter] = useState('submitted');
  const [orgs, setOrgs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async after => {
    setLoading(true);
    setError(null);
    try {
      const data = await orgApi.listOrganizations({ status: 'active', limit: 100, cursor: after });
      setOrgs(prev => (after ? [...prev, ...(data.organizations || [])] : data.organizations || []));
      setCursor(data.nextCursor || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  const shown = orgs.filter(
    o => o.compliance && (filter === 'all' || o.compliance.status === 'submitted')
  );

  return (
    <section>
      <div className={cn(ui.row, 'mb-4')} role="group" aria-label="Show">
        {FILTERS.map(f => (
          <button
            key={f.id}
            type="button"
            className={cn(filter === f.id ? ui.btnPrimary : ui.btnSecondary, ui.btnSm)}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {error && <ErrorAlert error={error} />}
      {loading && orgs.length === 0 ? (
        <Spinner label="Loading profiles..." />
      ) : shown.length === 0 ? (
        <EmptyState
          message={
            filter === 'submitted'
              ? 'No profiles are waiting for review.'
              : 'No organization has a profile yet.'
          }
        />
      ) : (
        shown.map(org => (
          <ReviewCard
            key={org.orgId}
            org={org}
            onReviewed={updated =>
              setOrgs(prev => prev.map(o => (o.orgId === updated.orgId ? updated : o)))
            }
          />
        ))
      )}
      {cursor && (
        <button
          type="button"
          className={cn(ui.btnSecondary, ui.btnSm)}
          disabled={loading}
          onClick={() => load(cursor)}
        >
          {loading ? 'Loading...' : 'Look through more organizations'}
        </button>
      )}
    </section>
  );
}
